import React, { useImperativeHandle, useMemo } from "react";
import { StyleSheet, useWindowDimensions, View } from "react-native";
import { SwiperFlatList } from "react-native-swiper-flatlist";
import { useAppContext } from "../../Universal/API/Context-API/AppContext";
import ImageSwiperNavigation from "./ImageSwiperNavigation";
import ImageSwiperSlide from "./ImageSwiperSlide";
import { useImageSwiperController } from "./useImageSwiperController";

interface ImageSwiperProps {
  images: string[];
  slideWidth?: number;
  onPress?: ({ index }: { index: number }) => any;
  onIndexChange?: ({ index }: { index: number }) => any;
}

export interface handleImageSwiper {
  goToIndex: (x: number) => any;
}

const ImageSwiper = React.forwardRef<handleImageSwiper, ImageSwiperProps>(
  ({ images, slideWidth, onPress, onIndexChange }, ref) => {
    const { scaleAll, isSmallVersion, isMediumVersion } = useAppContext();
    const { width } = useWindowDimensions();

    const styles = useMemo(() => getStyles(scaleAll), [scaleAll]);
    const itemWidth = slideWidth ?? width;
    const itemHeight = isSmallVersion ? 200 : isMediumVersion ? 350 : slideWidth ? 620 : 500;
    const imagesSignature = useMemo(() => images.join("|"), [images]);

    const {
      activeIndex,
      commitIndexChange,
      focusWrapper,
      goNext,
      goPrev,
      goToIndex,
      swiperRef,
      syncIndexFromOffset,
      webFocusableProps,
      wrapperRef,
    } = useImageSwiperController({
      images,
      itemWidth,
      onIndexChange,
    });

    useImperativeHandle(
      ref,
      () => ({
        goToIndex: (idx: number) => goToIndex(idx, true),
      }),
      [goToIndex]
    );

    if (!images || images.length === 0) return null;

    return (
      <View style={{ width: itemWidth, alignSelf: "center" }}>
        <View ref={wrapperRef} style={styles.wrapper} {...webFocusableProps}>
          <SwiperFlatList
            key={imagesSignature}
            ref={swiperRef}
            data={images}
            style={{ width: itemWidth, height: itemHeight }}
            keyExtractor={(item, index) => `${item}-${index}`}
            getItemLayout={(_, index) => ({
              length: itemWidth,
              offset: itemWidth * index,
              index,
            })}
            renderItem={({ item, index }) => (
              <ImageSwiperSlide
                image={item}
                index={index}
                itemHeight={itemHeight}
                itemWidth={itemWidth}
                scaleAll={scaleAll}
                slideInnerStyle={styles.slideInner}
                slidePressableStyle={styles.slidePressable}
                onFocus={focusWrapper}
                onPress={onPress}
              />
            )}
            onScroll={(event) => {
              syncIndexFromOffset(event?.nativeEvent?.contentOffset?.x ?? 0);
            }}
            scrollEventThrottle={16}
          />

          <ImageSwiperNavigation
            activeIndex={activeIndex}
            imagesCount={images.length}
            navButtonStyle={styles.navButton}
            navOverlayStyle={styles.navOverlay}
            scaleAll={scaleAll}
            sideStyle={styles.side}
            onFocus={focusWrapper}
            onNext={goNext}
            onPrev={goPrev}
          />
        </View>
      </View>
    );
  }
);

const getStyles = (scaleAll: number) =>
  StyleSheet.create({
    wrapper: {
      position: "relative",
      width: "100%",
      overflow: "hidden",
    },
    slidePressable: {
      width: "100%",
      height: "100%",
    },
    slideInner: {
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden",
    },
    navOverlay: {
      position: "absolute",
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      paddingHorizontal: 8 * scaleAll,
      pointerEvents: "box-none",
    },
    side: {
      justifyContent: "center",
      alignItems: "center",
    },
    navButton: {
      padding: 10 * scaleAll,
      borderRadius: 999,
      backgroundColor: "rgba(255,255,255,0.95)",
      shadowColor: "#000",
      shadowOpacity: 0.15,
      shadowRadius: 4,
      elevation: 3,
    },
    pagination: {
      bottom: 8 * scaleAll,
      gap: 6 * scaleAll,
    },
    paginationDot: {
      width: 8 * scaleAll,
      height: 8 * scaleAll,
      borderRadius: 999,
      backgroundColor: "rgba(0,0,0,0.18)",
      marginHorizontal: 0,
    },
    paginationDotActive: {
      backgroundColor: "#111111",
    },
  });

export default ImageSwiper;
